// Тур по приложению (HQ-2338) — то, что живёт в главном чанке: когда звать ленивый кусок тура (AppTour.tsx). Здесь
// только признаки запуска и маленькое общее состояние; решение «кому что» и вид — в куске тура.
//   — старт: только /tab/matches, после свежего ответа config и первой страницы вкладки (не по таймеру);
//   — не при запуске по ссылке (startapp, dev ?route=) и не при входе сразу в мир;
//   — меню «Тур по приложению» — запрос menu: ведёт на /tab/matches и запускает тур с шага 1.
import { useSyncExternalStore } from 'react';

export const TOUR_ROUTE = '/tab/matches';

interface State {
  /** Первая страница «Матчей» пришла из сети в этом запуске (не кэш прошлого запуска). */
  ready: boolean;
  /** Повтор из меню ждёт «Матчей». */
  menu: boolean;
  /** Тур на экране. */
  active: boolean;
  /** Кусок тура ищет якоря шагов (до 1,5 с). */
  resolving: boolean;
  /** Автоматический показ этого запуска решён (показан, не нужен или запрещён) — больше не пробуем. */
  autoDone: boolean;
}

let state: State = { ready: false, menu: false, active: false, resolving: false, autoDone: false };
const listeners = new Set<() => void>();
function set(patch: Partial<State>) {
  const next = { ...state, ...patch };
  if (Object.keys(patch).every((k) => next[k as keyof State] === state[k as keyof State])) return;
  state = next;
  listeners.forEach((l) => l());
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export const useTourState = (): State => useSyncExternalStore(subscribe, () => state, () => state);
export const tourState = (): State => state;

export const setMatchesReady = (ready: boolean) => set({ ready });
/** Меню → «Тур по приложению»: тур с шага 1, как только «Матчи» готовы. */
export const requestMenuTour = () => set({ menu: true });
/** Якоря тура (data-tutorial на вкладках, пунктах шапки, первой строке) рисуются только пока тур ищет цели или идёт:
 *  разметка остальных экранов — как без тура (селекторы axe, эталоны, чужие тесты). */
export const useTourAnchors = (): boolean => useSyncExternalStore(subscribe, () => state.resolving || state.active, () => state.resolving || state.active);
export const tourResolving = () => set({ resolving: true });
export const tourShown = () => set({ active: true, resolving: false, autoDone: true });
/** Тур закрыт (Done, Skip, «Назад» или кусок сняли); menu — закрылся повтор из меню: запрос снят. */
export const tourEnded = (menu: boolean) => set(menu ? { active: false, resolving: false, menu: false, autoDone: true } : { active: false, resolving: false, autoDone: true });
/** Автоматического показа в этом запуске не будет (нет тура в config, конфиг не пришёл, запуск по ссылке). */
export const autoTourOff = () => set({ autoDone: true, resolving: false });

/** Запуск, при котором тур сам не начинается: по ссылке (startapp / dev ?route=) или сразу в мир игры. */
export const launchAllowsTour = (p: { launchParam: string | null; firstPath: string | null }): boolean =>
  !p.launchParam && !(p.firstPath ?? '').startsWith('/world');

/** В config есть шаги тура (подробный разбор — в куске тура). */
export const configHasTour = (config: { app_tour?: { steps?: unknown } } | undefined): boolean =>
  Array.isArray(config?.app_tour?.steps) && config.app_tour.steps.length > 0;

/** Звать ли кусок тура сейчас. Пейвол и онбординг — свои маршруты: пока они открыты, мы не на «Матчах» и ждём. */
export function wantTour(p: {
  pathname: string; hasTour: boolean; configFresh: boolean; state: State; autoAllowed: boolean;
}): boolean {
  if (p.state.active) return true;
  if (p.pathname !== TOUR_ROUTE || !p.hasTour || !p.configFresh || !p.state.ready) return false;
  return p.state.menu || (p.autoAllowed && !p.state.autoDone);
}

/** Тур может вот-вот начаться или идёт: лист «На главный экран» (HQ-725) поверх него не показываем. */
export const tourHolds = (s: State, autoAllowed: boolean): boolean => s.active || s.menu || (autoAllowed && !s.autoDone);
